import { useEffect, useState, useCallback } from "react";
import {
    Award, RefreshCw, Search, X, IndianRupee, CheckCircle2, Clock,
} from "lucide-react";
import { Select } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { DetailDrawer } from "../../components/engage/DetailDrawer";
import {
    ENGAGE_REFERRALS_URL,
    ENGAGE_REFERRAL_URL,
    ENGAGE_REFERRAL_MARK_PAID_URL,
} from "../../constants/api";

dayjs.extend(relativeTime);

const STATUS_OPTIONS = [
    { value: '',                    label: 'All statuses' },
    { value: 'referred',            label: 'Referred' },
    { value: 'contacted',           label: 'Contacted' },
    { value: 'appointment_booked',  label: 'Appointment Booked' },
    { value: 'converted',           label: 'Converted' },
    { value: 'dropped',             label: 'Dropped' },
];

const PAID_OPTIONS = [
    { value: '',      label: 'All' },
    { value: 'false', label: 'Unpaid only' },
    { value: 'true',  label: 'Paid only' },
];

const STATUS_BADGE = {
    referred:            'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
    contacted:           'bg-cyan-100 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300',
    appointment_booked:  'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
    converted:           'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300',
    dropped:             'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

// ─── DetailDrawer field schema ─────────────────────────────────────────────
const REFERRAL_FIELDS = [
    { key: 'id',                       label: 'ID',                type: 'readonly' },
    { key: 'referring_doctor_name',    label: 'Referring doctor',  type: 'readonly' },
    { key: 'patient_name',             label: 'Patient',           type: 'readonly' },
    { key: 'referral_date',            label: 'Referral date',     type: 'date' },
    { key: 'clinical_reason',          label: 'Clinical reason',   type: 'textarea' },
    { key: 'expected_specialty',       label: 'Expected specialty',type: 'text' },
    { key: 'status',                   label: 'Status',            type: 'select',
      options: STATUS_OPTIONS.filter(o => o.value) },
    { key: 'dropped_reason',           label: 'Dropped reason',    type: 'text' },
    { key: 'incentive_amount',         label: 'Incentive ₹',       type: 'number', step: '0.01' },
    { key: 'incentive_paid',           label: 'Paid',              type: 'readonly' },
    { key: 'incentive_paid_at',        label: 'Paid at',           type: 'readonly' },
    { key: 'incentive_notes',          label: 'Payment notes',     type: 'text' },
    { key: 'created_date',             label: 'Created',           type: 'datetime' },
];

const ReferralsList = () => {
    const { fetch: apiFetch, create, update, remove } = useApiMethods({ throwOnFail: true });

    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: '', specialty: '', incentive_paid: '' });

    const [selected, setSelected] = useState(null);

    // Mark-paid modal state
    const [paidRow, setPaidRow]     = useState(null);
    const [paidForm, setPaidForm]   = useState({ incentive_amount: '', incentive_notes: '' });
    const [paidBusy, setPaidBusy]   = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status)          params.status         = filters.status;
            if (filters.specialty)       params.specialty      = filters.specialty;
            if (filters.incentive_paid)  params.incentive_paid = filters.incentive_paid;
            const res = await apiFetch(ENGAGE_REFERRALS_URL, params);
            setRows(res?.results || []);
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const handleMarkPaid = async () => {
        if (!paidRow) return;
        setPaidBusy(true);
        try {
            const payload = {};
            if (paidForm.incentive_amount) payload.incentive_amount = paidForm.incentive_amount;
            if (paidForm.incentive_notes)  payload.incentive_notes  = paidForm.incentive_notes;
            await create(ENGAGE_REFERRAL_MARK_PAID_URL(paidRow.id), payload);
            ToastMsg('success', 'Incentive marked as paid');
            setPaidRow(null);
            setPaidForm({ incentive_amount: '', incentive_notes: '' });
            load();
        } catch (e) {
            ToastMsg('error', 'Failed to mark as paid');
        } finally {
            setPaidBusy(false);
        }
    };

    return (
        <ContainerCard>
            {/* ── Header ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-600" />
                    <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Referrals</h1>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({rows.length})</span>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            {/* ── Filters ──────────────────────────────────────────────── */}
            <div className="flex flex-wrap gap-2 mb-3 items-center px-1">
                <Select
                    style={{ minWidth: 180 }}
                    value={filters.status}
                    onChange={(v) => setFilters(f => ({ ...f, status: v }))}
                    options={STATUS_OPTIONS}
                    placeholder="Status"
                />
                <Input
                    placeholder="Specialty"
                    value={filters.specialty}
                    onChange={(e) => setFilters(f => ({ ...f, specialty: e.target.value }))}
                    className="h-9 w-40"
                />
                <Select
                    style={{ minWidth: 140 }}
                    value={filters.incentive_paid}
                    onChange={(v) => setFilters(f => ({ ...f, incentive_paid: v }))}
                    options={PAID_OPTIONS}
                    placeholder="Payment"
                />
                {(filters.status || filters.specialty || filters.incentive_paid) && (
                    <Button variant="ghost" size="sm" onClick={() => setFilters({ status: '', specialty: '', incentive_paid: '' })}>
                        <X className="w-3 h-3 mr-1" /> Clear
                    </Button>
                )}
            </div>

            {/* ── Table ────────────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Date</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Referring Doctor</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Patient</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Specialty</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Clinical reason</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Status</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Incentive</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500 italic">
                                No referrals match the current filters.
                            </td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id}
                                onClick={() => setSelected(r)}
                                className="cursor-pointer border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="text-gray-900 dark:text-gray-100">{r.referral_date ? dayjs(r.referral_date).format('DD MMM YYYY') : '—'}</div>
                                    <div className="text-xs text-gray-500">{r.referral_date ? dayjs(r.referral_date).fromNow() : ''}</div>
                                </td>
                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                    Dr. {r.referring_doctor_name}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                    {r.patient_name || <span className="text-gray-400 italic">not converted</span>}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.expected_specialty || '—'}</td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 max-w-xs truncate" title={r.clinical_reason}>
                                    {r.clinical_reason || '—'}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded font-medium ${STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-700'}`}>
                                        {r.status_display || r.status}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <div className="inline-flex items-center gap-0.5 text-gray-900 dark:text-gray-100 font-medium">
                                        <IndianRupee className="w-3 h-3" />
                                        {parseFloat(r.incentive_amount || 0).toLocaleString('en-IN')}
                                    </div>
                                    {r.incentive_paid && (
                                        <div className="text-xs text-green-600 flex items-center justify-end gap-0.5">
                                            <CheckCircle2 className="w-3 h-3" /> paid
                                            {r.incentive_paid_at && <span className="text-gray-400 ml-1">{dayjs(r.incentive_paid_at).fromNow()}</span>}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}>
                                    {!r.incentive_paid && parseFloat(r.incentive_amount || 0) > 0 ? (
                                        <Button size="sm" variant="outline" onClick={() => {
                                            setPaidRow(r);
                                            setPaidForm({ incentive_amount: r.incentive_amount, incentive_notes: '' });
                                        }}>
                                            Mark Paid
                                        </Button>
                                    ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ── Detail drawer ────────────────────────────────────────── */}
            <DetailDrawer
                open={!!selected}
                onClose={() => setSelected(null)}
                record={selected}
                title={selected && `Dr. ${selected.referring_doctor_name} — ${selected.referral_date || ''}`}
                subtitle="Doctor Referral"
                fields={REFERRAL_FIELDS}
                actions={
                    selected && !selected.incentive_paid && parseFloat(selected.incentive_amount || 0) > 0
                        ? [{
                            label:   'Mark Paid',
                            icon:    IndianRupee,
                            onClick: (r) => {
                                setSelected(null);
                                setPaidRow(r);
                                setPaidForm({ incentive_amount: r.incentive_amount, incentive_notes: '' });
                            },
                        }]
                        : []
                }
                onSave={async (patch) => {
                    try {
                        const body = { ...selected, ...patch };
                        await update(ENGAGE_REFERRAL_URL(selected.id), body);
                        ToastMsg('success', 'Referral updated');
                        setSelected(null);
                        load();
                    } catch (e) {
                        ToastMsg('error', 'Failed to update referral');
                    }
                }}
                onDelete={async () => {
                    try {
                        await remove(ENGAGE_REFERRAL_URL(selected.id));
                        ToastMsg('success', 'Referral deleted');
                        load();
                    } catch (e) {
                        ToastMsg('error', 'Failed to delete');
                    }
                }}
            />

            {/* ── Mark paid modal ──────────────────────────────────────── */}
            <DialogModal
                open={!!paidRow}
                onClose={() => setPaidRow(null)}
                onSave={handleMarkPaid}
                title={`Mark incentive paid — Dr. ${paidRow?.referring_doctor_name || ''}`}
                saveLabel={paidBusy ? 'Saving…' : 'Confirm payment'}
                loading={paidBusy}
                width="440px"
            >
                <div className="space-y-3 text-sm">
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Amount (₹)</label>
                        <Input
                            type="number"
                            step="0.01"
                            value={paidForm.incentive_amount}
                            onChange={(e) => setPaidForm(f => ({ ...f, incentive_amount: e.target.value }))}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            Override the recorded amount if you paid a different value.
                        </p>
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Payment reference</label>
                        <Input
                            value={paidForm.incentive_notes}
                            onChange={(e) => setPaidForm(f => ({ ...f, incentive_notes: e.target.value }))}
                            placeholder="UPI txn id, cheque number, etc."
                        />
                    </div>
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default ReferralsList;
